import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DollarSign, FileText, Briefcase, ArrowRight } from 'lucide-react';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import api from '@/lib/api';
import type { ApiResponse, ContractWithParties, JobWithAgency } from '@hirenfire/shared';

interface WorkerStats {
  totalEarnings: number;
  activeContracts: number;
  pendingApplications: number;
}

export function WorkerDashboard() {
  const [stats, setStats] = useState<WorkerStats>({
    totalEarnings: 0,
    activeContracts: 0,
    pendingApplications: 0,
  });
  const [contracts, setContracts] = useState<ContractWithParties[]>([]);
  const [recommendedJobs, setRecommendedJobs] = useState<JobWithAgency[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      try {
        const [statsRes, contractsRes, jobsRes] = await Promise.all([
          api.get<ApiResponse<WorkerStats>>('/dashboard/worker/stats'),
          api.get<ApiResponse<ContractWithParties[]>>('/contracts?status=ACTIVE&limit=5'),
          api.get<ApiResponse<JobWithAgency[]>>('/jobs?limit=6&status=OPEN'),
        ]);
        if (statsRes.data.data) setStats(statsRes.data.data);
        if (contractsRes.data.data) setContracts(contractsRes.data.data);
        if (jobsRes.data.data) setRecommendedJobs(jobsRes.data.data);
      } catch {
        // fallback to defaults
      } finally {
        setIsLoading(false);
      }
    }
    fetchData();
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-white">Worker Dashboard</h1>
        <p className="text-gray-400 mt-1">Your work overview at a glance</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardBody>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-success-500/10 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-success-400" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Total Earnings</p>
                <p className="text-2xl font-bold text-white">
                  ${stats.totalEarnings.toLocaleString()}
                </p>
              </div>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-primary-500/10 flex items-center justify-center">
                <Briefcase className="w-6 h-6 text-primary-400" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Active Contracts</p>
                <p className="text-2xl font-bold text-white">{stats.activeContracts}</p>
              </div>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-warning-500/10 flex items-center justify-center">
                <FileText className="w-6 h-6 text-warning-400" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Pending Applications</p>
                <p className="text-2xl font-bold text-white">{stats.pendingApplications}</p>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>

      <Card>
        <CardHeader action={
          <Link to="/contracts">
            <Button variant="ghost" size="sm" rightIcon={<ArrowRight className="w-4 h-4" />}>
              View All
            </Button>
          </Link>
        }>
          Active Contracts
        </CardHeader>
        <CardBody>
          {contracts.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No active contracts</p>
          ) : (
            <div className="space-y-3">
              {contracts.map((contract) => (
                <Link
                  key={contract.id}
                  to={`/contracts/${contract.id}`}
                  className="flex items-center justify-between p-4 rounded-lg bg-dark-700/50 hover:bg-dark-700 transition-colors"
                >
                  <div>
                    <p className="font-medium text-white">{contract.job.title}</p>
                    <p className="text-sm text-gray-400">with {contract.agency.displayName}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-white">
                      ${contract.totalAmount} {contract.currency}
                    </p>
                    <Badge variant="success">{contract.status}</Badge>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader action={
          <Link to="/jobs">
            <Button variant="ghost" size="sm" rightIcon={<ArrowRight className="w-4 h-4" />}>
              Browse All
            </Button>
          </Link>
        }>
          Recommended Jobs
        </CardHeader>
        <CardBody>
          {recommendedJobs.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No recommended jobs right now</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {recommendedJobs.map((job) => (
                <Link
                  key={job.id}
                  to={`/jobs/${job.id}`}
                  className="p-4 rounded-lg bg-dark-700/50 hover:bg-dark-700 transition-colors"
                >
                  <p className="font-medium text-white mb-1">{job.title}</p>
                  <p className="text-sm text-gray-400 mb-2">{job.agency.displayName}</p>
                  <div className="flex items-center gap-2">
                    <Badge variant="primary">{job.type}</Badge>
                    <Badge variant="purple">{job.category}</Badge>
                    <span className="text-sm text-success-400 ml-auto">
                      ${job.budget} {job.currency}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
